import styled from 'styled-components';
import { baseVariables } from '../../styles';
import { OCarouselTriptychImageOrientation } from './OCarouselTriptych.types';

const { mediaQuery, spacers } = baseVariables;

export const Container = styled.div`
  padding-top: var(--general-spacer-04, 1.5rem);
  padding-bottom: var(--general-spacer-04, 1.5rem);

  @media ${mediaQuery.md} {
    padding-top: var(--general-spacer-05, 2rem);
    padding-bottom: var(--general-spacer-05, 2rem);
  }
`;

interface TriptychWrapperProps {
  $orientation?: OCarouselTriptychImageOrientation;
}

export const TriptychWrapper = styled.div.attrs<TriptychWrapperProps>(({ $orientation }) => ({
  'data-orientation': $orientation || OCarouselTriptychImageOrientation.Horizontal,
}))<TriptychWrapperProps>`
  position: relative;
  width: 100%;

  /* Allow slides to overflow container */
  .swiper {
    overflow: visible;

    --loading-animation-duration: 1s;
    transition: opacity var(--loading-animation-duration) linear;
    opacity: 0;
  }
  .swiper-initialized {
    opacity: 1;
  }

  /*
   * !important is required on slide widths because Swiper sets an inline
   * style="width: ..." on each .swiper-slide when using slidesPerView: 'auto'.
   * Inline styles win over stylesheet rules without !important, so without it
   * our responsive widths (and the vertical-orientation override below) would
   * be ignored once Swiper initializes.
   */
  .swiper-slide {
    width: 283px !important;

    @media ${mediaQuery.md} {
      width: 473px !important;
    }

    @media ${mediaQuery.xl} {
      width: 754px !important;
    }
  }

  &[data-orientation='Vertical'] .swiper-slide {
    width: 126px !important;

    @media ${mediaQuery.md} {
      width: 210px !important;
    }

    @media ${mediaQuery.xl} {
      width: 335px !important;
    }
  }

  .swiper-slide picture {
    --scale-factor: 0.9;
    --offset-base: 5%; /* Base offset for prev/next slides */
    overflow-x: visible;
    transform: scale(var(--scale-factor));
    transform-origin: center;
    transition: transform 600ms ease-in-out;
  }

  /* Scale animation on images - inactive slides scaled down */
  .swiper-slide-prev picture {
    transform: scale(var(--scale-factor)) translateX(calc(var(--offset-base) / var(--scale-factor)));
  }
  .swiper-slide-next picture {
    transform: scale(var(--scale-factor)) translateX(calc(var(--offset-base) * -1 / var(--scale-factor)));
  }

  .swiper-slide:not(.swiper-slide-active, .swiper-slide-next) {
    /* Prev - 1 (first sibling before prev slide) */
    &:has(+ .swiper-slide-prev) {
      picture {
        transform: scale(var(--scale-factor)) translateX(calc(var(--offset-base) * 3 / var(--scale-factor)));
      }
    }
    /* Prev - 2 (second sibling before prev slide) */
    &:has(+ .swiper-slide + .swiper-slide-prev) {
      picture {
        transform: scale(var(--scale-factor)) translateX(calc(var(--offset-base) * 5 / var(--scale-factor)));
      }
    }
  }
  .swiper-slide-next {
    /* Next + 1 (first sibling after next slide) */
    & + .swiper-slide:not(.swiper-slide-active, .swiper-slide-prev) {
      picture {
        transform: scale(var(--scale-factor)) translateX(calc(var(--offset-base) * -3 / var(--scale-factor)));
      }
      /* Next + 2 (second sibling after next slide) */
      & + .swiper-slide:not(.swiper-slide-active, .swiper-slide-prev) {
        picture {
          transform: scale(var(--scale-factor)) translateX(calc(var(--offset-base) * -5 / var(--scale-factor)));
        }
      }
    }
  }

  /* Active slide: image at normal size */
  .swiper-slide-active picture {
    transform: scale(1);
  }

  /* Z-index for layering */
  .swiper-slide-active {
    z-index: 2;
  }
`;

export const TriptychSlide = styled.div`
  position: relative;

  /* Ensure image fills slide */
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

export const HeaderWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-inline: auto;
  padding-inline: ${spacers?.spacers?.[3] ?? '1rem'};

  @media ${mediaQuery.sm} {
    padding-inline: ${spacers?.spacers?.[4] ?? '1.5rem'};

    > div {
      flex-direction: row;
    }
  }

  @media ${mediaQuery.md} {
    padding-inline: ${spacers?.spacers?.[5] ?? '2rem'};
  }

  @media ${mediaQuery.lg} {
    padding-inline: ${spacers?.spacers?.[4] ?? '1.5rem'};
  }

  @media ${mediaQuery.xl} {
    padding-inline: ${spacers?.spacers?.[5] ?? '2rem'};
  }
`;

interface ControlsContainerProps {
  $orientation?: OCarouselTriptychImageOrientation;
}

export const ControlsContainer = styled.div<ControlsContainerProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--general-spacer-03, 1rem);
  margin-top: ${({ $orientation }) =>
    $orientation === OCarouselTriptychImageOrientation.Vertical
      ? 'var(--general-spacer-03, 1rem)'
      : 'var(--general-spacer-04, 1.5rem)'};
`;
